import type { AdminBillingManager, AdminDiscount } from '@/state/useAdminBilling';
import type { DiscountFormManager } from '@/state/useDiscountForm';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DataTable, useDataControl } from '@/components/DataTable';
import { ConfirmDialog } from '@/components/Dialogs/ConfirmDialog';
import { cn } from 'helpers/cn';
import { formatMinorUnits } from 'helpers/formatters';
import { isDiscountEnded } from '@/state/useAdminBilling';
import { useDiscountForm } from '@/state/useDiscountForm';
import { ActionButton, Field, FIELD_CONTROL, MICRO_LABEL_SHAPE, Panel } from '../../Dashboard/ConsolePanel';
import { SectionStatusLine } from '../../Dashboard/Settings/Billing/SectionStatusLine';

type DiscountsPanelProps = { manager: AdminBillingManager };

const DURATION_OPTIONS = [
  { value: 'once', label: 'Once' },
  { value: 'repeating', label: 'Repeating (N months)' },
  { value: 'forever', label: 'Forever' },
] as const;

const TYPE_OPTIONS = [
  { value: 'percentage', label: 'Percentage' },
  { value: 'fixed', label: 'Fixed amount' },
] as const;

const discountValue = (discount: AdminDiscount) =>
  discount.type === 'percentage'
    ? `${((discount.basisPoints ?? 0) / 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}%`
    : discount.amount != null
      ? formatMinorUnits(discount.amount, discount.currency?.toUpperCase() ?? null)
      : 'Fixed';

const discountDuration = (discount: AdminDiscount) =>
  discount.duration === 'repeating'
    ? `Repeating, ${discount.durationInMonths ?? '?'} month${discount.durationInMonths === 1 ? '' : 's'}`
    : discount.duration === 'once'
      ? 'Once'
      : 'Forever';

/**
 * Campaign codes, straight against Polar's discounts API; nothing is mirrored
 * into our own DB (see discounts.ts). "Disable" ends the discount now rather
 * than deleting it, since Polar has no separate active flag, and it asks first:
 * a customer mid-checkout with the code loses it.
 */
export const DiscountsPanel = ({ manager }: DiscountsPanelProps) => {
  const form = useDiscountForm({ createDiscount: manager.createDiscount });
  const { discounts, disablingDiscount } = manager;

  return (
    <Panel
      title="Discounts"
      description="Campaign codes in Polar."
      actions={
        <Button size="sm" className="h-8 rounded-lg text-body" onClick={form.open}>
          Create discount
        </Button>
      }
    >
      {!discounts.data ? (
        <SectionStatusLine
          status={discounts}
          loadingText="Loading discounts..."
          errorText="Discounts could not be loaded."
        />
      ) : (
        <DiscountRows discounts={discounts.data} manager={manager} />
      )}

      <CreateDiscountDialog form={form} currency={manager.currency} />

      <ConfirmDialog
        open={!!disablingDiscount}
        title="Disable this discount?"
        confirmLabel="Disable"
        confirmVariant="destructive"
        onConfirm={manager.confirmDisableDiscount}
        onCancel={manager.cancelDisableDiscount}
      >
        <p className="text-foreground m-0 text-body">
          <strong>{disablingDiscount?.name}</strong>
          {disablingDiscount?.code ? ` (code ${disablingDiscount.code})` : ''} ends now and can no longer be redeemed.
          Customers who already redeemed it keep it. This cannot be undone from here.
        </p>
      </ConfirmDialog>
    </Panel>
  );
};

type DiscountRowsProps = { discounts: AdminDiscount[]; manager: AdminBillingManager };

const DiscountRows = ({ discounts, manager }: DiscountRowsProps) => {
  const dataController = useDataControl<AdminDiscount>(discounts);
  const now = Date.now();

  return (
    <DataTable<AdminDiscount>
      dataController={dataController}
      uniqueKey="id"
      noDataMessage="No discounts yet."
      columns={[
        { label: 'Name', field: 'name', sortKey: 'name', mobile: 'title' },
        { label: 'Code', field: 'code', format: ({ item }) => item.code || '-' },
        { label: 'Value', format: ({ item }) => <span className="num">{discountValue(item)}</span> },
        { label: 'Duration', field: 'duration', format: ({ item }) => discountDuration(item) },
        {
          label: 'Redemptions',
          format: ({ item }) => (
            <span className="num">
              {item.redemptionsCount ?? 0}
              {item.maxRedemptions != null ? ` / ${item.maxRedemptions}` : ''}
            </span>
          ),
        },
        {
          label: 'Status',
          format: ({ item }) => {
            const ended = isDiscountEnded(item, now);
            return (
              <span
                className={cn(
                  MICRO_LABEL_SHAPE,
                  'rounded-pill inline-flex w-fit border px-2 py-0.5',
                  ended ? 'border-line text-dim' : 'border-ok-line text-ok',
                )}
              >
                {ended ? 'Disabled' : 'Active'}
              </span>
            );
          },
        },
        {
          label: ' ',
          mobile: 'action',
          format: ({ item }) =>
            isDiscountEnded(item, now) ? null : (
              <ActionButton
                onClick={() => manager.requestDisableDiscount(item)}
                ariaLabel={`Disable discount ${item.name}`}
              >
                Disable
              </ActionButton>
            ),
        },
      ]}
    />
  );
};

type CreateDiscountDialogProps = { form: DiscountFormManager; currency: string | null };

const CreateDiscountDialog = ({ form, currency }: CreateDiscountDialogProps) => {
  const { form: values, setField } = form;

  return (
    <ConfirmDialog
      open={form.isOpen}
      title="Create discount"
      confirmLabel="Create"
      disabled={!!form.blockingReason}
      disabledReason={form.blockingReason}
      onConfirm={form.submit}
      onCancel={form.close}
    >
      <div className="grid gap-4">
        <Field label="Name" htmlFor="discount-name">
          <Input
            id="discount-name"
            value={values.name}
            onChange={(ev) => setField('name', ev.target.value)}
            placeholder="e.g. Launch campaign"
            className={FIELD_CONTROL}
          />
        </Field>
        <Field label="Code (optional)" htmlFor="discount-code" hint="Generated by Polar if left blank.">
          <Input
            id="discount-code"
            value={values.code}
            onChange={(ev) => setField('code', ev.target.value)}
            placeholder="e.g. LAUNCH25"
            className={FIELD_CONTROL}
          />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Type" htmlFor="discount-type">
            <Select
              value={values.type}
              onValueChange={(value) => setField('type', value === 'fixed' ? 'fixed' : 'percentage')}
            >
              <SelectTrigger id="discount-type" className={FIELD_CONTROL}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TYPE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Duration" htmlFor="discount-duration">
            <Select
              value={values.duration}
              onValueChange={(value) =>
                setField('duration', value === 'repeating' ? 'repeating' : value === 'forever' ? 'forever' : 'once')
              }
            >
              <SelectTrigger id="discount-duration" className={FIELD_CONTROL}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DURATION_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </div>
        {values.duration === 'repeating' && (
          <Field label="Months" htmlFor="discount-months" hint="1 to 36.">
            <Input
              id="discount-months"
              type="number"
              min="1"
              max="36"
              step="1"
              value={values.durationInMonths}
              onChange={(ev) => setField('durationInMonths', ev.target.value)}
              className={FIELD_CONTROL}
            />
          </Field>
        )}
        {values.type === 'percentage' ? (
          <Field label="Percentage off" htmlFor="discount-percent">
            <Input
              id="discount-percent"
              type="number"
              min="0.01"
              max="100"
              step="0.01"
              value={values.percent}
              onChange={(ev) => setField('percent', ev.target.value)}
              className={FIELD_CONTROL}
            />
          </Field>
        ) : (
          <Field label={`Fixed amount off${currency ? ` (${currency})` : ''}`} htmlFor="discount-amount">
            <Input
              id="discount-amount"
              type="number"
              min="0.01"
              step="0.01"
              value={values.fixedAmount}
              onChange={(ev) => setField('fixedAmount', ev.target.value)}
              className={FIELD_CONTROL}
            />
          </Field>
        )}
        <Field label="Max redemptions (optional)" htmlFor="discount-max-redemptions">
          <Input
            id="discount-max-redemptions"
            type="number"
            min="1"
            step="1"
            value={values.maxRedemptions}
            onChange={(ev) => setField('maxRedemptions', ev.target.value)}
            className={FIELD_CONTROL}
          />
        </Field>
      </div>
    </ConfirmDialog>
  );
};
